import {DataLayerParams, trackEvent} from "./analytics";

// Business events for GTM. One place for names and parameters, so the agency's
// tracking plan and the code cannot drift apart. Parameters must never carry
// personal data, patient data, free text or invite ids (see README.md).

export const AnalyticsEvents = {
    login: "login",
    signUp: "sign_up",
    sessionStart: "session_start_emdr",
    sessionComplete: "session_complete_emdr",
    inviteCreate: "invite_create",
    share: "share",
    ctaClick: "cta_click",
} as const;

// All sessions are remote today: the patient joins through a link.
// TODO(team): add "in_office" once sessions can run with the EMDRlink device only.
export type SessionType = "online";
export type ShareMethod = "copy_link" | "copy_invitation";
export type CtaName = "upgrade_plan" | "advertise" | "edit_profile_on_website";

const QUEUE_KEY = "emdrlink_analytics_queue";
const LOGIN_METHOD_KEY = "emdrlink_login_method";

// Auth0 `sub` is "<connection>|<id>"; only the connection part is used.
const METHOD_BY_CONNECTION: Record<string, string> = {
    auth0: "email",
    email: "email_code",
    sms: "sms",
    "google-oauth2": "google",
    apple: "apple",
    facebook: "facebook",
    windowslive: "microsoft",
    linkedin: "linkedin",
};

export function loginMethodFromSub(sub?: string): string {
    const connection = sub && sub.includes("|") ? sub.split("|")[0] : "";
    if (!connection) {
        return "unknown";
    }
    return METHOD_BY_CONNECTION[connection] ?? "other";
}

function readQueue(): Array<{event: string; params: DataLayerParams}> {
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(QUEUE_KEY) || "[]");
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/** Keeps an event across the full reload that often follows the Auth0 callback. */
export function queueEvent(event: string, params: DataLayerParams = {}): void {
    try {
        window.sessionStorage.setItem(QUEUE_KEY, JSON.stringify([...readQueue(), {event, params}]));
    } catch {
        trackEvent(event, params);
    }
}

export function flushQueuedEvents(): void {
    const queue = readQueue();
    try {
        window.sessionStorage.removeItem(QUEUE_KEY);
    } catch {
        /* storage unavailable: nothing was queued */
    }
    queue.forEach(({event, params}) => trackEvent(event, params));
}

/** Auth0 onRedirectCallback: a completed sign-in (manual or SSO from emdrlink.pl). */
export function trackLoginRedirect(user?: {sub?: string}): void {
    const method = loginMethodFromSub(user?.sub);
    try {
        window.sessionStorage.setItem(LOGIN_METHOD_KEY, method);
    } catch {
        /* sign_up then reports "unknown" */
    }
    queueEvent(AnalyticsEvents.login, {method});
}

/** First completion of the profile form (terms accepted) = account created in the app. */
export function trackSignUp(): void {
    let method = "unknown";
    try {
        method = window.sessionStorage.getItem(LOGIN_METHOD_KEY) || "unknown";
    } catch {
        /* keep unknown */
    }
    trackEvent(AnalyticsEvents.signUp, {method});
}

export function trackSessionStart(sessionType: SessionType): void {
    trackEvent(AnalyticsEvents.sessionStart, {session_type: sessionType});
}

export function trackSessionComplete(sessionType: SessionType, durationSec: number, setsCount: number): void {
    trackEvent(AnalyticsEvents.sessionComplete, {
        session_type: sessionType,
        session_duration_sec: durationSec,
        sets_count: setsCount,
    });
}

export function trackInviteCreated(): void {
    trackEvent(AnalyticsEvents.inviteCreate);
}

export function trackInviteShared(method: ShareMethod): void {
    trackEvent(AnalyticsEvents.share, {method, content_type: "session_invite"});
}

export function trackCtaClick(ctaName: CtaName): void {
    trackEvent(AnalyticsEvents.ctaClick, {cta_name: ctaName});
}
